import { create } from 'zustand';
import type { User } from 'firebase/auth';
import {
  onAuthChange,
  signInWithEmail,
  signUpWithEmail,
  signInWithGoogle,
  signInWithApple,
  signOut,
  resetPassword,
  handleRedirectResult,
} from '../services/firebase';
import type { UserProfile } from '../services/firebase';
import { analytics, identifyUser } from '../services/analytics';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isInitialized: boolean;
  /** True once the Firestore onboarding-status check has resolved (or errored).
   *  Guards against routing before we know whether onboarding is complete on a new device. */
  isOnboardingReady: boolean;
  error: string | null;
  // Subscription / trial state (read from ID token claims + Firestore)
  isPremium: boolean;
  isInTrial: boolean;
  trialEnd: number | null;       // Unix timestamp ms
  foundingMember: boolean;

  // Actions
  initialize: () => () => void;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithApple: () => Promise<void>;
  logout: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  clearError: () => void;
  devLogin: () => void;
  refreshPremiumStatus: () => Promise<void>;
}

// Dev mode mock user for testing without Firebase
const DEV_MOCK_USER = {
  uid: 'dev-user-123',
  email: 'dev@example.com',
  displayName: 'Dev User',
  emailVerified: true,
} as User;

async function readPremiumStatus(user: User, existingProfile?: UserProfile | null) {
  // Force-refresh ID token to get latest custom claims from webhook
  const idTokenResult = await user.getIdTokenResult(true);
  const claims = idTokenResult.claims as Record<string, unknown>;

  const isPremium = claims['premium'] === true;
  const foundingMember = claims['foundingMember'] === true;

  // Use pre-fetched profile if provided, otherwise fetch from Firestore
  // (claims don't carry trialEnd)
  const profile = existingProfile !== undefined
    ? existingProfile
    : await (await import('../services/firebase')).getUserProfile(user.uid);

  const trialEnd = profile?.trialEnd ?? null;
  const isInTrial = trialEnd !== null && Date.now() < trialEnd;

  return { isPremium, isInTrial, trialEnd, foundingMember };
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true,
  isInitialized: false,
  isOnboardingReady: false,
  error: null,
  isPremium: false,
  isInTrial: false,
  trialEnd: null,
  foundingMember: false,

  initialize: () => {
    // Check for dev bypass in localStorage
    const devBypass = localStorage.getItem('dev-auth-bypass');
    if (devBypass === 'true') {
      set({ isInitialized: true, isOnboardingReady: true, isLoading: false, user: DEV_MOCK_USER });
      return () => {};
    }

    // Check for redirect result first (for social sign-in redirects)
    handleRedirectResult()
      .then((result) => {
        if (result?.user) {
          // Reset isOnboardingReady: false so the LoadingScreen holds until
          // onAuthChange fires and completes the Firestore onboarding-status check.
          // Without this, the null-auth onAuthChange could have already set
          // isOnboardingReady: true, causing a premature route evaluation.
          set({ user: result.user, isLoading: false, isInitialized: true, isOnboardingReady: false });
        }
      })
      .catch((error) => {
        console.error('Redirect result error:', error);
      });

    // Set up auth state listener
    const unsubscribe = onAuthChange(async (user) => {
      if (!user?.uid) {
        // No authenticated user — nothing async to wait for, mark fully ready immediately
        set({ user: null, isLoading: false, isInitialized: true, isOnboardingReady: true });
        return;
      }

      // Authenticated — explicitly reset isOnboardingReady: false so the LoadingScreen holds
      // until the Firestore check below resolves. This guards against the redirect-flow race
      // where onAuthChange(null) fires first and sets isOnboardingReady: true prematurely,
      // which would cause routing before hasCompletedOnboarding is synced from Firestore.
      set({ user, isLoading: false, isInitialized: true, isOnboardingReady: false });

      // Sync onboarding status from Firestore to localStorage
      if (user?.uid) {
        // Fetch profile once; reuse for both onboarding sync and premium status
        let profile: UserProfile | null = null;
        try {
          console.log('🔄 Checking Firestore for onboarding status...');
          const { getUserProfile } = await import('../services/firebase');
          profile = await getUserProfile(user.uid);

          console.log('📊 Firestore profile:', {
            exists: !!profile,
            onboardingCompletedAt: profile?.onboardingCompletedAt
          });

          if (profile?.onboardingCompletedAt) {
            // User has completed onboarding in Firestore, sync to localStorage
            const { useOnboardingStore } = await import('./onboardingStore');
            const currentStatus = useOnboardingStore.getState().hasCompletedOnboarding;
            console.log('📍 Current onboarding status:', currentStatus);

            if (!currentStatus) {
              useOnboardingStore.getState().markAsCompleted();
              console.log('✅ Synced onboarding status from Firestore');
            } else {
              console.log('✅ Onboarding already marked as completed');
            }
          } else {
            console.log('⚠️ No onboarding completion in Firestore');
          }
        } catch (error) {
          console.error('❌ Error syncing onboarding status:', error);
        }

        // Onboarding status is now resolved (success or error) — safe to route
        set({ isOnboardingReady: true });

        // Read premium/trial status, passing the already-fetched profile to avoid a second Firestore read
        try {
          const status = await readPremiumStatus(user, profile);
          // Guard against stale callbacks running after the user has logged out
          if (useAuthStore.getState().user?.uid === user.uid) {
            set(status);
            // Sync premium status to PostHog for cohort segmentation
            identifyUser(user.uid, {
              isPremium: status.isPremium,
              isInTrial: status.isInTrial,
            });
          }
        } catch (e) {
          console.error('Failed to read premium status:', e);
        }

        // Silently refresh FCM token if notification permission is already granted.
        // This ensures tokens stay registered across new devices/browsers without
        // requiring the user to revisit Notifications settings each time.
        if ('Notification' in window && Notification.permission === 'granted') {
          try {
            const { requestNotificationPermission, saveFcmToken } = await import('../services/firebase/messaging');
            const token = await requestNotificationPermission();
            if (token && useAuthStore.getState().user?.uid === user.uid) {
              await saveFcmToken(user.uid, token);
            }
          } catch (e) {
            // Non-fatal — notification registration failure should not affect app startup
            console.warn('FCM token refresh skipped:', e);
          }
        }
      }
    });

    return unsubscribe;
  },

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const result = await signInWithEmail(email, password);
      set({ user: result.user, isLoading: false });
      analytics.login('email');
      identifyUser(result.user.uid);
    } catch (error: unknown) {
      const message = getAuthErrorMessage(error);
      set({ error: message, isLoading: false });
      throw new Error(message);
    }
  },

  register: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const result = await signUpWithEmail(email, password);
      set({ user: result.user, isLoading: false });
      analytics.signUp('email');
      identifyUser(result.user.uid);
    } catch (error: unknown) {
      const message = getAuthErrorMessage(error);
      set({ error: message, isLoading: false });
      throw new Error(message);
    }
  },

  loginWithGoogle: async () => {
    set({ isLoading: true, error: null });
    try {
      const result = await signInWithGoogle();
      set({ user: result.user, isLoading: false });
      analytics.login('google');
      identifyUser(result.user.uid);
    } catch (error: unknown) {
      const message = getAuthErrorMessage(error);
      set({ error: message, isLoading: false });
      throw new Error(message);
    }
  },

  loginWithApple: async () => {
    set({ isLoading: true, error: null });
    try {
      const result = await signInWithApple();
      set({ user: result.user, isLoading: false });
      analytics.login('apple');
      identifyUser(result.user.uid);
    } catch (error: unknown) {
      const message = getAuthErrorMessage(error);
      set({ error: message, isLoading: false });
      throw new Error(message);
    }
  },

  logout: async () => {
    set({ isLoading: true, error: null });
    // Clear dev bypass on logout
    localStorage.removeItem('dev-auth-bypass');
    // Note: We don't reset onboarding state on logout
    // Users should only redo onboarding if they explicitly choose to or if it's a new account
    try {
      await signOut();
      set({ user: null, isLoading: false });
      analytics.logout();
    } catch (error: unknown) {
      // Even if Firebase logout fails, clear local state in dev mode
      set({ user: null, isLoading: false });
      analytics.logout();
    }
  },

  devLogin: () => {
    if (import.meta.env.DEV) {
      localStorage.setItem('dev-auth-bypass', 'true');
      set({ user: DEV_MOCK_USER, isInitialized: true, isLoading: false });
    }
  },

  sendPasswordReset: async (email: string) => {
    set({ isLoading: true, error: null });
    try {
      await resetPassword(email);
      set({ isLoading: false });
    } catch (error: unknown) {
      const message = getAuthErrorMessage(error);
      set({ error: message, isLoading: false });
      throw new Error(message);
    }
  },

  clearError: () => set({ error: null }),

  refreshPremiumStatus: async () => {
    const { user } = useAuthStore.getState();
    if (!user) return;
    try {
      const status = await readPremiumStatus(user);
      set(status);
    } catch (e) {
      console.error('Failed to refresh premium status:', e);
    }
  },
}));

// Helper to convert Firebase auth errors to user-friendly messages
function getAuthErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const code = (error as { code?: string }).code;

    switch (code) {
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/operation-not-allowed':
        return 'This sign-in method is not enabled.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/invalid-credential':
        return 'Invalid email or password.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in was cancelled.';
      case 'auth/popup-blocked':
        return 'Pop-up was blocked. Please allow pop-ups and try again.';
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with this email using a different sign-in method.';
      default:
        return error.message || 'An error occurred. Please try again.';
    }
  }
  return 'An unexpected error occurred.';
}

// Convenience hook for common auth checks
export function useAuth() {
  const {
    user,
    isLoading,
    isInitialized,
    isOnboardingReady,
    error,
    login,
    register,
    loginWithGoogle,
    loginWithApple,
    logout,
    sendPasswordReset,
    clearError,
    devLogin,
  } = useAuthStore();

  return {
    user,
    isLoading,
    isInitialized,
    isOnboardingReady,
    isAuthenticated: !!user,
    error,
    login,
    register,
    loginWithGoogle,
    loginWithApple,
    logout,
    sendPasswordReset,
    clearError,
    devLogin,
  };
}
